'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { css } from '@flipster/styled-system/css';
import { numberFormatter } from '@flipster/utils/num';

import { orderbookStateBunja } from '@web/app/[locale]/(web)/trade/_misc/state';

import { formBunja } from '../order-form/perpetual/bunja/form';
import { spotFormBunja } from '../order-form/spot/bunja/form';
import { tradeSymbolBunja } from '../symbol';
import { OrderbookCenterPrice } from './OrderbookCenterPrice';
import { rowHeight } from './OrderbookRow';
import { OrderbookRows } from './OrderbookRows';
import { orderbookDataBunja } from './bunja';

const scrollTolerance = 5;
const midHeight = 50;

export const OrderbookList = () => {
  const centerPriceRef = useRef<HTMLDivElement>(null);
  const [showRecenter, setShowRecenter] = useState(false);
  const [priceDirection, setPriceDirection] = useState<'up' | 'down'>('up');
  const [hoveredRow, setHoveredRow] = useState<{ type: 'ask' | 'bid'; index: number } | null>(null);
  const isCenteredRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);

  // 계산은 전부 orderbookDataBunja에서 — 여기서는 consume만 한다
  const {
    centerPriceAtom,
    markPriceAtom,
    askItemsAtom,
    bidItemsAtom,
    maxAskSizeAtom,
    maxBidSizeAtom,
    realAskCountAtom,
    realBidCountAtom,
    limitPendingPositionsAtom,
  } = useBunja(orderbookDataBunja);
  const { displayModeAtom, selectedTickSizeAtom } = useBunja(orderbookStateBunja);
  const centerPrice = useAtomValue(centerPriceAtom);
  const markPrice = useAtomValue(markPriceAtom);
  const normalizedAsks = useAtomValue(askItemsAtom);
  const normalizedBids = useAtomValue(bidItemsAtom);
  const maxAskSize = useAtomValue(maxAskSizeAtom);
  const maxBidSize = useAtomValue(maxBidSizeAtom);
  const realAskCount = useAtomValue(realAskCountAtom);
  const realBidCount = useAtomValue(realBidCountAtom);
  const pendingPositions = useAtomValue(limitPendingPositionsAtom);
  const displayMode = useAtomValue(displayModeAtom);
  const selectedTickSize = useAtomValue(selectedTickSizeAtom);

  const prevCenterPriceRef = useRef<string>(centerPrice);

  // mid가 자연 scroll 중앙(scrollHeight/2)에 오려면 `topPad - botPad = bidArea - askArea`
  // 조건이 필요. row 수가 적은 쪽에만 차이만큼 padding을 주고, 많은 쪽은 padding 없음.
  const askArea = realAskCount * rowHeight;
  const bidArea = realBidCount * rowHeight;
  const topPadHeight = Math.max(0, bidArea - askArea);
  const bottomPadHeight = Math.max(0, askArea - bidArea);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 컨테이너의 visible height를 추적해서, 실제(real) row만으로 viewport에 다 들어가면
  // scroll을 disable함. 빈 placeholder는 viewport 밖으로 잘려도 OK — 다만 사용자가
  // 그쪽으로 scroll해서 가는 건 막아야 함.
  const [clientHeight, setClientHeight] = useState(0);
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setClientHeight(container.clientHeight);
    const observer = new ResizeObserver(([entry]) => {
      setClientHeight(entry.contentRect.height);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const visibleRealTotal = (realAskCount + realBidCount) * rowHeight + midHeight;
  const scrollDisabled = clientHeight > 0 && visibleRealTotal <= clientHeight;

  const { symbolInfoAtom, symbol } = useBunja(tradeSymbolBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const {
    priceTypeAtom: perpPriceTypeAtom,
    limitPriceAtom: perpLimitPriceAtom,
    triggerPriceAtom: perpTriggerPriceAtom,
    focusedTpSlFieldAtom: perpFocusedTpSlFieldAtom,
    autofillTpSlAtom: perpAutofillTpSlAtom,
  } = useBunja(formBunja);
  const {
    priceTypeAtom: spotPriceTypeAtom,
    limitPriceAtom: spotLimitPriceAtom,
    triggerPriceAtom: spotTriggerPriceAtom,
  } = useBunja(spotFormBunja);
  const perpPriceType = useAtomValue(perpPriceTypeAtom);
  const spotPriceType = useAtomValue(spotPriceTypeAtom);
  const setPerpLimitPrice = useSetAtom(perpLimitPriceAtom);
  const setSpotLimitPrice = useSetAtom(spotLimitPriceAtom);
  const setPerpTriggerPrice = useSetAtom(perpTriggerPriceAtom);
  const setSpotTriggerPrice = useSetAtom(spotTriggerPriceAtom);
  const perpFocusedTpSlField = useAtomValue(perpFocusedTpSlFieldAtom);
  const autofillPerpTpSl = useSetAtom(perpAutofillTpSlAtom);

  const isSpot = symbolInfo?.isSpot;
  const priceType = isSpot ? spotPriceType : perpPriceType;
  const setLimitPrice = isSpot ? setSpotLimitPrice : setPerpLimitPrice;
  const setTriggerPrice = isSpot ? setSpotTriggerPrice : setPerpTriggerPrice;
  const focusedTpSlField = isSpot ? null : perpFocusedTpSlField;

  const getCenterScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (!container) return 0;
    // Ask와 bid의 row 수가 같으므로 centerPrice는 scroll content의 정확한 중앙에 위치.
    // centerPrice element를 직접 측정하지 않는 이유: position:sticky라서
    // getBoundingClientRect가 잘못된 값을 반환함.
    return (container.scrollHeight - container.clientHeight) / 2;
  };

  const scrollToCenter = (smooth: boolean = true) => {
    if (scrollContainerRef.current) {
      isProgrammaticScrollRef.current = true;
      scrollContainerRef.current.scrollTo({
        top: getCenterScrollPosition(),
        behavior: smooth ? 'smooth' : 'auto',
      });
      isCenteredRef.current = true;
      setShowRecenter(false);
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    }
  };

  // centerPrice 변화 추적
  useEffect(() => {
    const prevPrice = parseFloat(prevCenterPriceRef.current);
    const currentPrice = parseFloat(centerPrice);

    if (!isNaN(prevPrice) && !isNaN(currentPrice) && prevPrice !== currentPrice) {
      setPriceDirection(currentPrice > prevPrice ? 'up' : 'down');
    }

    prevCenterPriceRef.current = centerPrice;
  }, [centerPrice]);

  // 초기 스크롤 위치를 중앙으로 설정 (애니메이션 없이)
  useLayoutEffect(() => {
    scrollToCenter(false);
  }, []);

  // 컨테이너 height가 변경되면(panel resize) 중앙으로 재정렬.
  // rAF로 여러 resize frame을 묶어서 layout thrashing 방지.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let rafId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        scrollToCenter(false);
      });
    });
    observer.observe(container);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  // symbol 또는 tickSize 변경시 스크롤 위치를 중앙으로 설정 (애니메이션 없이)
  useEffect(() => {
    scrollToCenter(false);
  }, [symbol, selectedTickSize]);

  // Center price row가 중앙에서 벗어났는지 체크 (사용자 스크롤만 추적)
  useEffect(() => {
    const container = scrollContainerRef.current;

    if (!container) return;

    const checkPosition = () => {
      if (isProgrammaticScrollRef.current) return;

      const currentScrollTop = container.scrollTop;
      const targetCenter = getCenterScrollPosition();
      const isAtCenter = Math.abs(currentScrollTop - targetCenter) <= scrollTolerance;

      isCenteredRef.current = isAtCenter;
      setShowRecenter(!isAtCenter);
    };

    // 초기 체크
    checkPosition();

    // 스크롤 이벤트 리스너
    container.addEventListener('scroll', checkPosition);

    return () => {
      container.removeEventListener('scroll', checkPosition);
    };
  }, []);

  const handleAskMouseEnter = useCallback((index: number) => {
    setHoveredRow({ type: 'ask', index });
  }, []);

  const handleBidMouseEnter = useCallback((index: number) => {
    setHoveredRow({ type: 'bid', index });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredRow(null);
  }, []);

  const handleOrderbookMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isSpot && focusedTpSlField) {
        e.preventDefault();
      }
    },
    [isSpot, focusedTpSlField],
  );

  const fillPrice = useCallback(
    (price: string) => {
      if (!isSpot && focusedTpSlField) {
        autofillPerpTpSl({ type: focusedTpSlField, value: price });
        return;
      }
      if (priceType === 'limit') setLimitPrice(price);
      if (priceType === 'trigger') setTriggerPrice(price);
    },
    [isSpot, focusedTpSlField, autofillPerpTpSl, priceType, setLimitPrice, setTriggerPrice],
  );

  // items 배열을 deps로 잡으면 매 tick 콜백이 재생성돼 row memo가 깨지므로 ref로 최신 배열을 참조
  const normalizedAsksRef = useRef(normalizedAsks);
  normalizedAsksRef.current = normalizedAsks;
  const normalizedBidsRef = useRef(normalizedBids);
  normalizedBidsRef.current = normalizedBids;

  const handleAskClick = useCallback(
    (index: number) => fillPrice(normalizedAsksRef.current[index]?.price?.toString() || '0'),
    [fillPrice],
  );

  const handleBidClick = useCallback(
    (index: number) => fillPrice(normalizedBidsRef.current[index]?.price?.toString() || '0'),
    [fillPrice],
  );

  const handleClickCenterPrice = useCallback(
    () =>
      fillPrice(
        numberFormatter(centerPrice, {
          method: 'round-half-up',
          precision: symbolInfo?.pricePrecision,
          toFixed: true,
          withComma: false,
        }),
      ),
    [fillPrice, centerPrice, symbolInfo?.pricePrecision],
  );

  return (
    <div
      ref={scrollContainerRef}
      onMouseDown={handleOrderbookMouseDown}
      style={{ overflowY: scrollDisabled ? 'hidden' : 'auto' }}
      className={css({
        overflowAnchor: 'none',
        flex: 1,
        p: 16,
        pb: 0,
      })}
    >
      <div style={{ height: topPadHeight }} aria-hidden />
      <OrderbookRows
        type='ask'
        items={normalizedAsks}
        maxSize={maxAskSize}
        pendingPositions={pendingPositions}
        hoveredIndex={hoveredRow?.type === 'ask' ? hoveredRow.index : null}
        onMouseEnter={handleAskMouseEnter}
        onMouseLeave={handleMouseLeave}
        displayMode={displayMode}
        onClick={handleAskClick}
        selectedTickSize={selectedTickSize}
      />

      <OrderbookCenterPrice
        ref={centerPriceRef}
        centerPrice={centerPrice}
        markPrice={markPrice}
        precision={symbolInfo?.pricePrecision}
        priceDirection={priceDirection}
        showRecenter={showRecenter}
        onRecenter={scrollToCenter}
        onClickCenterPrice={handleClickCenterPrice}
      />

      <OrderbookRows
        type='bid'
        items={normalizedBids}
        maxSize={maxBidSize}
        pendingPositions={pendingPositions}
        hoveredIndex={hoveredRow?.type === 'bid' ? hoveredRow.index : null}
        onMouseEnter={handleBidMouseEnter}
        onMouseLeave={handleMouseLeave}
        displayMode={displayMode}
        onClick={handleBidClick}
        selectedTickSize={selectedTickSize}
      />
      <div style={{ height: bottomPadHeight }} aria-hidden />
    </div>
  );
};
